import React, { useState, useRef } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import { ToastContainer, Toast, Alert } from 'react-bootstrap';
import { uploadFiles } from '../../apis/fileApi';

export function FilesUpload({ show, handleClose, jobId, refreshFiles }) {
    const [files, setFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadStatuses, setUploadStatuses] = useState([]);
    const [toast, setToast] = useState({ show: false, title: '', message: '', variant: 'success' });
    const fileInputRef = useRef(null); // ref to reset file input display

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
        setUploadStatuses((prevStatuses) => [...prevStatuses, ...selectedFiles.map(() => ({ status: 'pending', message: '' }))]);
        setErrorMessage('');
    };

    const handleRemoveFile = (fileToRemove) => {
        setFiles((prevFiles) => {
            const indexToRemove = prevFiles.indexOf(fileToRemove);
            setUploadStatuses((prevStatuses) => prevStatuses.filter((_, idx) => idx !== indexToRemove));
            return prevFiles.filter((_, idx) => idx !== indexToRemove);
        });
    };

    const updateStatus = (index, status) => {
        setUploadStatuses((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], status };
            return next;
        });
    };

    const updateMessage = (index, message) => {
        setUploadStatuses((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], message };
            return next;
        });
    };

    // Reset all state including file input display
    const resetAll = () => {
        setFiles([]);
        setUploadStatuses([]);
        setErrorMessage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async () => {
        if (!files || files.length === 0) {
            setErrorMessage('Please select at least one file to upload.');
            return;
        }

        setErrorMessage('');
        setIsLoading(true);
        setUploadStatuses(files.map(() => ({ status: 'pending', message: '' })));

        const uploadItems = files.map((file) => ({ file, name: file.name, type: file.type, size: file.size }));

        try {
            const results = await uploadFiles(uploadItems, updateStatus, updateMessage, jobId);
            const errors = results.filter((item) => item.status !== 'success');
            if (errors.length === 0) {
                setToast({ show: true, title: 'Upload complete', message: 'All files uploaded successfully.', variant: 'success' });
                resetAll(); // clear everything on full success
                if (refreshFiles) refreshFiles();
            } else {
                setToast({ show: true, title: 'Upload completed', message: `${errors.length} file(s) failed to upload.`, variant: 'danger' });
            }
        } catch (error) {
            setToast({ show: true, title: 'Upload failed', message: error?.message || 'Unexpected upload error.', variant: 'danger' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Upload Files for Job ID: {jobId}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
								<Form.Group controlId="formFileMultiple" className="mb-3">
									<Form.Label>Select files to upload</Form.Label>
										<div className="d-flex align-items-center gap-2 border border-solid rounded">
												<Form.Control
														type="file"
														multiple
														onChange={handleFileChange}
														ref={fileInputRef}
														style={{ display: 'none', }}
												/>
												<Button variant="light" size="sm" onClick={() => fileInputRef.current?.click()}>
														Choose Files
												</Button>
												<span className="text-muted">
														{files.length === 0 ? 'No files chosen' : `${files.length} file${files.length > 1 ? 's' : ''} chosen`}
												</span>
										</div>
								</Form.Group>

                {errorMessage && <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>{errorMessage}</Alert>}

                <ListGroup>
                    {files.map((file, idx) => (
                        <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="fw-semibold">{file.name}</div>
                                <div className="text-muted">{Math.round((file.size || 0) / 1024)} KB</div>
                                {uploadStatuses[idx]?.status && (
                                    <span className={`badge bg-${
                                        uploadStatuses[idx].status === 'uploadComplete' ? 'success' :
                                        uploadStatuses[idx].status === 'error' ? 'danger' : 'warning'
                                    } me-2`}>
                                        {uploadStatuses[idx].status}
                                    </span>
                                )}
                                {uploadStatuses[idx]?.message && (
                                    <div className="small text-muted">{uploadStatuses[idx].message}</div>
                                )}
                            </div>
                            <div>
                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFile(file)}>Remove</Button>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="primary" onClick={handleSave} disabled={isLoading}>{isLoading ? 'Uploading...' : 'Upload Files'}</Button>
            </Modal.Footer>

            <ToastContainer className="p-3 position-absolute bottom-0 end-0">
                <Toast show={toast.show} bg={toast.variant} onClose={() => setToast({ ...toast, show: false })} delay={4000} autohide>
                    <Toast.Header>
                        <strong className="me-auto">{toast.title}</strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'danger' ? 'text-white' : ''}>{toast.message}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Modal>
    );
}
